/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import type { ApiListResponse } from '@mairistem/administres-utils';

import { BASE_URL_ETATCIVIL } from '@/constants';
import type { ActeIndividu, TypeActe } from '@/types';

type PartenairesResponse = {
  partenaire1?: ActeIndividu | null;
  partenaire2?: ActeIndividu | null;
  perePartenaire1?: ActeIndividu | null;
  merePartenaire1?: ActeIndividu | null;
  perePartenaire2?: ActeIndividu | null;
  merePartenaire2?: ActeIndividu | null;
};

/**
 * Récupère les individus liés à un acte donné.
 * Appeler `get(typeActe, identifiantActe)` pour déclencher la recherche.
 */
const useActeIndividus = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Array<ActeIndividu>>([]);

  const reset = useCallback(() => {
    setData([]);
    setLoading(false);
  }, []);

  const get = useCallback((typeActe: TypeActe, identifiantActe: number) => {
    setLoading(true);
    setData([]);

    if (typeActe === 'pacs') {
      request
        .get<unknown, { data: PartenairesResponse }>(
          `${BASE_URL_ETATCIVIL}/conventions/${identifiantActe}/partenaires`,
        )
        .then(({ data: response }) => {
          const roles: [ActeIndividu | null | undefined, string][] = [
            [response.partenaire1, 'Partenaire 1'],
            [response.partenaire2, 'Partenaire 2'],
            [response.perePartenaire1, 'Père partenaire 1'],
            [response.merePartenaire1, 'Mère partenaire 1'],
            [response.perePartenaire2, 'Père partenaire 2'],
            [response.merePartenaire2, 'Mère partenaire 2'],
          ];
          setData(
            roles
              .filter(([ind]) => ind != null)
              .map(
                ([ind, role]) =>
                  ({ ...ind, typeIndividuActe: role }) as ActeIndividu,
              ),
          );
        })
        .catch(() => {
          toast.error(
            'Une erreur est survenue lors du chargement des individus',
          );
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    request
      .get<unknown, ApiListResponse<ActeIndividu>>(
        `${BASE_URL_ETATCIVIL}/actes/${typeActe}/${identifiantActe}/individus`,
      )
      .then(({ data: individus }) => {
        setData(individus);
      })
      .catch(() => {
        toast.error('Une erreur est survenue lors du chargement des individus');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return {
    data,
    loading,
    get,
    reset,
  };
};

export default useActeIndividus;
